import React from "react";
import { ShieldCheck, Clock, Award, Star, Radar, Trash2, CheckCircle2, XCircle } from "lucide-react";
import { whyUs, platforms, steps, compare, testimonials, faqs } from "../mock";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./ui/accordion";

const iconMap = { ShieldCheck, Clock, Award, Star, Radar, Trash2 };

export function Section({ eyebrow, title, sub, children, id, className = "" }) {
  return (
    <section id={id} className={`py-20 md:py-24 ${className}`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {eyebrow && <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-3">{eyebrow}</div>}
        {title && <h2 className="text-3xl md:text-4xl font-bold text-[#0B2447] tracking-tight max-w-3xl">{title}</h2>}
        {sub && <p className="mt-4 text-slate-600 max-w-2xl text-lg leading-relaxed">{sub}</p>}
        <div className="mt-12">{children}</div>
      </div>
    </section>
  );
}

export function WhyUs() {
  return (
    <Section
      id="why"
      eyebrow="Why people choose us"
      title="The only reputation service with nothing to lose"
      sub="Most agencies charge thousands upfront and hope. We only get paid when your content is actually gone."
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {whyUs.map((w) => {
          const Icon = iconMap[w.icon] || ShieldCheck;
          return (
            <div key={w.title} className="group rounded-2xl border border-slate-200 bg-white p-7 hover:border-[#0B2447] hover:shadow-lg transition-all">
              <div className="w-11 h-11 rounded-lg bg-[#0B2447]/5 flex items-center justify-center mb-5 group-hover:bg-[#0B2447] group-hover:text-white transition-colors">
                <Icon className="w-5 h-5 text-[#0B2447] group-hover:text-white" />
              </div>
              <h3 className="font-semibold text-lg text-[#0B2447]">{w.title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{w.desc}</p>
            </div>
          );
        })}
      </div>
    </Section>
  );
}

export function Platforms() {
  return (
    <Section
      id="platforms"
      eyebrow="Platforms we work on"
      title="We remove content across every major platform"
      sub="From search results to review sites and forums. If it's damaging your reputation, we can help."
      className="bg-slate-50"
    >
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {platforms.map((p) => (
          <div key={p.name} className="group rounded-xl bg-white border border-slate-200 p-5 hover:border-[#0B2447] hover:-translate-y-0.5 transition-all cursor-pointer">
            <div className="w-10 h-10 rounded-lg bg-slate-100 group-hover:bg-[#0B2447] group-hover:text-white transition-colors flex items-center justify-center text-sm font-bold text-[#0B2447]">{p.initials}</div>
            <div className="mt-4 font-semibold text-[#0B2447]">{p.name}</div>
            <div className="text-xs text-slate-500 mt-0.5">{p.sub}</div>
          </div>
        ))}
      </div>
      <div className="mt-8 text-sm text-slate-500">
        Don't see your platform? We handle Yelp, BBB, Ripoff Report, news articles and more.
      </div>
    </Section>
  );
}

export function HowItWorks() {
  return (
    <Section
      id="how"
      eyebrow="How it works"
      title="Negative reviews removed in a few clicks"
      sub="A simple, transparent process, and you don't pay a cent until the content is gone."
    >
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {steps.map((s) => (
          <div key={s.n} className="relative rounded-2xl border border-slate-200 bg-white p-6">
            <div className="absolute -top-3 left-6 w-8 h-8 rounded-full bg-[#0B2447] text-white flex items-center justify-center text-sm font-semibold">{s.n}</div>
            <h3 className="mt-4 font-semibold text-[#0B2447]">{s.title}</h3>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.desc}</p>
          </div>
        ))}
      </div>
    </Section>
  );
}

export function Compare() {
  return (
    <Section
      id="compare"
      eyebrow="BMP vs. other agencies"
      title="A fairer way to protect your reputation"
      sub="See how our no-risk model compares to the typical reputation agency."
      className="bg-slate-50"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-slate-200 bg-white p-8">
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Other agencies</div>
          <div className="text-lg font-semibold text-slate-600 mb-6">The usual playbook</div>
          <ul className="space-y-3">
            {compare.others.map((c) => (
              <li key={c} className="flex items-start gap-3 text-sm text-slate-600"><XCircle className="w-5 h-5 text-slate-400 shrink-0" /> {c}</li>
            ))}
          </ul>
        </div>
        <div className="rounded-2xl border-2 border-[#0B2447] bg-[#0B2447] p-8 text-white">
          <div className="text-xs uppercase tracking-widest text-slate-300 mb-2">Brand Marketing Professionals</div>
          <div className="text-lg font-semibold mb-6">A model built around your outcome</div>
          <ul className="space-y-3">
            {compare.us.map((c) => (
              <li key={c} className="flex items-start gap-3 text-sm"><CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" /> {c}</li>
            ))}
          </ul>
        </div>
      </div>
    </Section>
  );
}

export function Testimonials() {
  return (
    <Section
      eyebrow="What our clients say"
      title="Trusted by thousands to set the record straight"
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {testimonials.slice(0, 3).map((t) => (
          <div key={t.name} className="rounded-2xl border border-slate-200 bg-white p-7">
            <div className="flex items-center gap-0.5 mb-4">
              {Array.from({ length: 5 }).map((_, i) => (<Star key={i} className="w-4 h-4 text-amber-400 fill-amber-400" />))}
            </div>
            <p className="text-slate-700 leading-relaxed">“{t.text}”</p>
            <div className="mt-6 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-sm font-semibold text-[#0B2447]">{t.initials}</div>
              <div>
                <div className="text-sm font-semibold text-[#0B2447]">{t.name}</div>
                <div className="text-xs text-slate-500">{t.role} · Verified</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}

export function FAQ() {
  return (
    <Section
      eyebrow="Questions & answers"
      title="Everything you need to feel confident"
      className="bg-slate-50"
    >
      <div className="max-w-3xl">
        <Accordion type="single" collapsible className="space-y-3">
          {faqs.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="bg-white rounded-xl border border-slate-200 px-5 data-[state=open]:border-[#0B2447]">
              <AccordionTrigger className="text-left text-[#0B2447] font-semibold hover:no-underline">{f.q}</AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </Section>
  );
}
